/**
 * When the tracking loop should be running (#8 D3, extended by #441).
 *
 * Stated once, as a pure function, because it was being re-decided in
 * App.vue's route watcher and again in the simulator's re-arm, and the two
 * drifted (#441 round three). A policy living in a template's callback
 * also cannot be tested without mounting the whole app, so it was not.
 *
 * Two callers, one function (#527): App.vue's route watcher and the
 * simulator's re-arm after a leg ends both ask `trackingStartMode`.
 */
export type GeolocationPermissionState = 'prompt' | 'granted' | 'denied' | 'unsupported';

/**
 * `'unsupported'` means the PERMISSIONS API is missing — pre-16.4 Safari,
 * older Android WebViews. Those do have geolocation, and the only way to
 * find out whether it works is to call it (PR #46 review F7), so they
 * count as permitted — once armed, because that call can itself prompt.
 *
 * #525: 'prompt' and 'unsupported' need a tap first (`armed`). The site
 * root now opens the tracker, and a location dialog before the first paint
 * is what the tap prevents. 'granted' never prompts, so it never waits.
 */
function permissionPermitsTracking(state: GeolocationPermissionState, armed: boolean): boolean {
  if (state === 'granted') return true;
  return armed && (state === 'prompt' || state === 'unsupported');
}

/**
 * A simulated leg needs no location permission at all, so a flip to
 * 'denied' — dismissing the still-open native prompt with Block — must not
 * stop the loop out from under one.
 */
/**
 * #527 — who drives the loop on /tracking.
 *
 *   - 'off'          not on the tracker: the loop stops.
 *   - 'keep'         a simulated leg owns the source; start() is a no-op.
 *   - 'real'         the browser's receiver may run (granted, or a tap
 *                    on prompt/unsupported).
 *   - 'approximate'  everything else, denied included: one city-level fix
 *                    from the network so the map is never blank and no
 *                    location dialog fires.
 */
export type TrackingStartMode = 'off' | 'keep' | 'real' | 'approximate';

export function trackingStartMode(
  routeName: unknown,
  permission: GeolocationPermissionState,
  simulating: boolean,
  armed: boolean
): TrackingStartMode {
  if (routeName !== 'tracking') return 'off';
  if (simulating) return 'keep';
  if (permissionPermitsTracking(permission, armed)) return 'real';
  return 'approximate';
}

/**
 * The GPS-stale banner is about a receiver that stopped delivering. An
 * approximate fix is old by design and a simulated one never ages, so the
 * banner applies to the browser's receiver only.
 */
export function gpsStaleBannerApplies(providerKind: string): boolean {
  return providerKind === 'geolocation';
}
